import React, { memo, useMemo } from "react";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import { PostingFormProps } from "./index.types";
import usePostingForm from "./indexModel";
import UserAvatar from "../UserAvatar";
import { useTranslation } from "react-i18next";
import { UpgradeToPro } from "../paywalls";
import ScheduledAtPicker from "../ScheduledPosts/ScheduledAtPicker";
import ButtonVariant from "../Button/ButtonVariant";
import ScheduleBtn from "../Button/ScheduleBtn";
import TagsSelect from "../inputs/SelectBox/TagsSelect";
import AttachmentPreview from "../AttachmentPreview";
import LocationsDropdown from "../LocationsDropdown";
import CreateMedia from "../CreateMedia";

interface ScheduleButtonProps {
  hasSchedule?: boolean;
  canSchedule?: boolean;
  scheduledInActive?: boolean;
  pickedSchedule?: number;
  onScheduleChange: (e: string | number) => void;
  paywallModalPro?: () => void;
}

const ScheduleButton = memo(
  ({
    hasSchedule,
    canSchedule,
    scheduledInActive,
    pickedSchedule,
    onScheduleChange,
    paywallModalPro,
  }: ScheduleButtonProps): React.ReactElement | null => {
    const { t } = useTranslation();

    if (!hasSchedule) {
      return null;
    }

    if (!canSchedule) {
      return (
        <ButtonVariant
          buttonType="secondary-outline"
          title={t("Schedule")}
          icon={<i className="ico ico-media" />}
          onClickFunc={paywallModalPro}
        />
      );
    }

    if (!scheduledInActive) {
      return (
        <ScheduledAtPicker
          value={pickedSchedule as any}
          onChange={(e): void => onScheduleChange(e)}
        />
      );
    }

    return (
      <ScheduleBtn
        scheduledTime={null}
        onScheduleChange={onScheduleChange}
        scheduledAt={pickedSchedule}
      />
    );
  },
);

ScheduleButton.displayName = "ScheduleButton";

const PostingForm = ({
  post,
  attachmentProps,
  isReply,
  className,
  expanded,
  profilePicture,
  userName,
  textAreaPlaceholder,
  postTextIsRequired,
  locationsIsRequired,
  scheduleIsRequired,
  canSelectMedia,
  canUploadMedia,
  hasAddMedia = true,
  canSchedule = true,
  hasSchedule = true,
  hasLocationSelect = true,
  disablePostButton,
  hasTagSelect = true,
  postButtonLabel = "Create",
  onPost,
  onCancel,
  errorMessage,
  scheduledInActive,
  isMove,
}: PostingFormProps) => {
  const { t } = useTranslation();
  const {
    postTextError,
    textAreaHandler,
    postingFormExpandHandler,
    postingFormIsExpanded,
    newPostData,
    postLocationsError,
    scheduleError,
    onTagsChange,
    onSelectMedia,
    onClearMedia,
    onScheduleChange,
    pickedSchedule,
    onSubmitFunction,
    onCancelHandler,
    attachments,
    onReorderAttachments,
    paywallModalHandler,
    paywallIsOpened,
    scheduleVisible,
  } = usePostingForm({
    post,
    attachmentProps,
    onPost,
    onCancel,
    expanded,
    postTextIsRequired,
    locationsIsRequired,
    scheduleIsRequired,
    scheduledInActive,
    hasSchedule,
    isReply,
  });

  const tagsValue = useMemo(
    () => (newPostData?.tags?.length > 0 ? newPostData?.tags : null),
    [newPostData?.tags],
  );

  return (
    <div className={cls(styles.container)}>
      <div
        className={cls(styles.content_wrapper, className, {
          [styles.expanded_container]: postingFormIsExpanded,
          [styles.is_reply]: isReply,
        })}
      >
        {profilePicture && (
          <div className={styles.posting_form_avatar}>
            <div className={styles.profile_picture_side}>
              <UserAvatar
                srcName={profilePicture}
                userName={userName}
                altName="avatar"
                className={styles.avatar}
                sizeHeight={44}
                sizeWidth={44}
              />
            </div>
          </div>
        )}
        <div className={styles.posting_form}>
          <textarea
            placeholder={textAreaPlaceholder ?? t("Create post...")}
            className={cls(styles.textArea, {
              [styles.input_error]: postTextError,
              [styles.expanded_text_area]: postingFormIsExpanded,
            })}
            onFocus={() => !postingFormIsExpanded && postingFormExpandHandler()}
            onChange={(e) => textAreaHandler(e.target.value)}
            value={newPostData?.details ?? ""}
          />
          {postTextIsRequired && postTextError && (
            <p className={styles.error_msg}>
              {t("Wouldn't you like to say something?")}
            </p>
          )}
          <div
            className={cls(styles.inner_content_wrapper, {
              [styles.expanded]: postingFormIsExpanded,
            })}
          >
            {postingFormIsExpanded && (
              <>
                {hasTagSelect && (
                  <TagsSelect
                    isCreatable
                    isClearable
                    value={tagsValue}
                    onChange={onTagsChange}
                    isMulti
                  />
                )}

                {attachments && (
                  <AttachmentPreview
                    uploadedMedia={attachments}
                    onClearMedia={onClearMedia}
                    hasPermission={canUploadMedia}
                    canSelectMedia={canSelectMedia}
                    onSelectMedia={onSelectMedia}
                    hasMultiSelect={!isReply && !attachmentProps}
                    canRemoveMedia={!attachmentProps}
                    onReorderMedia={onReorderAttachments}
                    alreadySelectedData={attachments}
                  />
                )}

                <span className={styles.footer_buttons}>
                  <div className={styles.buttons_wrapper}>
                    {hasLocationSelect && (
                      <LocationsDropdown
                        classNames={styles.location_btn}
                        hideLists={isMove}
                        hideSmartLists={isMove}
                      />
                    )}

                    {hasAddMedia && (
                      <CreateMedia
                        hasPermission={canUploadMedia}
                        titleContent={t("Media")}
                        icon={<i className="ico ico-media" />}
                        canSelectMedia={canSelectMedia}
                        onSelectMedia={onSelectMedia}
                        hasMultiSelect={!isReply}
                        alreadySelectedData={attachments}
                      />
                    )}
                    <ScheduleButton
                      hasSchedule={scheduleVisible}
                      canSchedule={canSchedule}
                      scheduledInActive={scheduledInActive}
                      pickedSchedule={pickedSchedule}
                      onScheduleChange={onScheduleChange}
                      paywallModalPro={paywallModalHandler}
                    />
                  </div>
                  <div className={styles.buttons_wrapper}>
                    <ButtonVariant
                      title={t("Cancel")}
                      buttonType="secondary-outline"
                      buttonStyles={styles.control_btn}
                      onClickFunc={onCancelHandler}
                    />
                    <ButtonVariant
                      title={postButtonLabel}
                      buttonType="primary"
                      disabled={disablePostButton}
                      onClickFunc={onSubmitFunction}
                    />
                  </div>
                </span>
                {locationsIsRequired && postLocationsError && (
                  <p className={styles.error_msg}>
                    {t("Please select a location")}
                  </p>
                )}
                {errorMessage && (
                  <p className={styles.error_msg}>{errorMessage}</p>
                )}
                {scheduleIsRequired && scheduleError && (
                  <p className={styles.error_msg}>
                    {t("Please select a schedule date")}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <UpgradeToPro show={paywallIsOpened} onClose={paywallModalHandler} />
    </div>
  );
};

export default PostingForm;
